<template>
  <Modal
      :close-on-background-click="false"
      :show-close="showModalClose"
      :modal-top="modalTop"
      @close-modal="checkBeforeClose"
  >
    <template
        v-if="currentStep !== STEPS.COMPLETE"
        #header
    >
      <portal-target name="entry_header">
        <CopyEntry
            v-if="!draft.data.id && copyChoices.length > 0 && currentStep === STEPS.ROLE"
            :choices="copyChoices"
            @selected="useSelectedEntry"
        />
      </portal-target>
    </template>
    <div
        v-if="event.data.deadline_edit_passed"
        class="uk-alert uk-margin"
    >
      {{ $t('events.deadline_edit_passed') }}
    </div>

      <UserAuthenticationFlow
          v-if="currentStep === STEPS.AUTHENTICATION"
          :ref="STEPS.AUTHENTICATION"
          v-model="draft.data.user_id"
          :entry="draft"
          :registrar-role="draft.data.registrar_role"
          :guardian-details="guardianDetails"
          :student-details="studentDetails"
          :address-details="addressDetails"
          @valid="setValid(STEPS.AUTHENTICATION, $event)"
      />

    <form-wrapper
        v-if="currentStep !== STEPS.AUTHENTICATION"
        ref="entryForm"
        @submit.prevent="saveEntry"
    >
        <StudentDetailsStep
            v-if="currentStep === STEPS.COMPETITOR"
            :ref="STEPS.COMPETITOR"
            v-model="studentDetails"
            :entry="entry"
            :entry-draft="draft"
            :event-id="eventId"
            :errors="studentErrors"
            :disabled="formShouldBeDisabled"
            @valid="setValid(STEPS.COMPETITOR, $event)"
        />

      <div v-if="currentStep === STEPS.ADDRESS">
        <GuardianDetailsStep
            :ref="STEPS.GUARDIAN"
            v-model="guardianDetails"
            :entry="entry"
            :errors="guardianErrors"
            :disabled="formShouldBeDisabled"
            @valid="setValid(STEPS.GUARDIAN, $event)"
        />

        <AddressDetailsStep
            :ref="STEPS.ADDRESS"
            v-model="addressDetails"
            :entry="entry"
            :errors="addressErrors"
            :disabled="formShouldBeDisabled"
            @valid="setValid(STEPS.ADDRESS, $event)"
        />
      </div>

        <RegistrarTypeChoice
            v-if="currentStep === STEPS.ROLE"
            :ref="STEPS.ROLE"
            v-model="draft.data.registrar_role"
            :entry="entry"
            :event="event"
            :disabled="formShouldBeDisabled"
            @valid="setValid(STEPS.ROLE, $event)"
        />

        <AuthenticateCompetitor
            v-if="currentStep === STEPS.COMPETITOR_ACCOUNT"
            :ref="STEPS.COMPETITOR_ACCOUNT"
            v-model="draft.data.competitor_id"
            :entry="draft"
            :student-details="studentDetails"
            :address-details="addressDetails"
            :disabled="formShouldBeDisabled"
            @valid="setValid(STEPS.COMPETITOR_ACCOUNT, $event)"
        />

      <div v-if="currentStep === STEPS.UPLOAD">
        <UploadStep
            v-if="!formShouldBeDisabled"
            :ref="STEPS.UPLOAD"
            :entry-ref="draft.data.reference"
            :entry="draft"
            :errors="uploadErrors"
            @valid="setValid(STEPS.UPLOAD, $event)"
            @select-files="$emit('select-files')"
            @set-backup-file="draft.data.backup_video_url = $event"
            @preview-video="$emit('preview-video')"
            @remove-attachment-reference="removeRef"
        />
        <p v-else>
          Videos can no longer be added to this entry.
        </p>
        <PermissionStep
            :ref="STEPS.WAIVER"
            v-model="draft.data.waiver"
            :entry="entry"
            :errors="permissionErrors"
            :disabled="formShouldBeDisabled"
            @valid="setValid(STEPS.WAIVER, $event)"
        />
      </div>

        <CustomWaiverStep
            v-if="currentStep === STEPS.CUSTOM_WAIVER"
            :default-waiver="event.custom_waiver"
            :entry="entry"
            @valid="setValid(STEPS.CUSTOM_WAIVER, $event)"
        />

      <FinishedStep
          v-if="currentStep === STEPS.COMPLETE"
          :entry="draft"
          @close="checkBeforeClose"
          @create-entry="$emit('create-entry')"
          @step-back="previousStep"
      />

      <Withdraw
          v-if="currentStep === STEPS.WITHDRAW"
          :entry="entry"
          @revert-step="revertStep"
      />
    </form-wrapper>

    <portal-target name="entry_before_footer"/>

    <template
        #footer
    >
      <portal-target name="entry_footer">
        <div
            v-if="currentStep !== STEPS.COMPLETE"
            class="uk-width-1-1"
        >
          <!--        <form-row class="uk-text-right">-->
          <div class="efFooter">
            <div class="efFooter__row">
              <a
                  v-if="!entry.isWithdrawn()"
                  class="efDeleteButton uk-button uk-button-outline uk-button-danger"
                  @click.prevent="removeEntry"
              >
                <span class="uk-icon-trash"/> {{ $t('events.cancel_entry') }}
              </a>
            </div>

            <div class="efFooter__row efNav">
              <span
                  v-if="hasPreviousStep"
                  class="uk-button uk-button-link efPrevious"
                  @click="previousStep"
              >previous step</span>
              <button
                  class="uk-button uk-button-primary efSubmit"
                  :class="{'efSubmit--disabled': !currentStepIsValid() && !formShouldBeDisabled}"
                  @click="nextStep"
                  v-html="submitButtonText"
              />
            </div>
          </div>
          <!--        </form-row>-->
        </div>
      </portal-target>
    </template>
  </Modal>
</template>

<script lang="ts">

import form from '../form/form-wrapper.vue';
import input from '../form/input-row.vue';
import formRow from '../form/form-row.vue';
import formError from '../form/form-error.vue';
import primaryButton from '../form/primary-button.vue';
import Attachment from '../uploader/Attachment.vue';
import EventEntryModel, {
  AddressDataMapper,
  create as createEntry,
  GuardianDataMapper,
  StudentDataMapper
} from "../../models/EventEntryModel";
import {Component, Mixins, Prop, Vue, Watch} from "vue-property-decorator";
import SelectRow from "../form/select-row.vue";
import Modal from '../misc/Modal.vue';
import CopyEntry from './CopyEntry.vue';
import StudentDetailsStep from "./StudentDetailsStep.vue";
import GuardianDetailsStep from "./GuardianDetailsStep.vue";
import RegistrarTypeChoice from "./RegistrarTypeChoice.vue";
import AddressDetailsStep from "./AddressDetailsStep.vue";
import UploadStep from "./UploadStep.vue";
import FinishedStep from "./FinishedStep.vue";
import PermissionStep from "./PermissionStep.vue";
import {STEPS, VALIDATION_STEPS} from "./steps";
import UserAuthenticationFlow from "./UserAuthenticationFlow.vue";
import AuthenticateCompetitor from "./AuthenticateCompetitor.vue";
import UserMixin from "./UserMixin";
import EventModel from "../../models/EventModel";
import PopupAuthentication from "./PopupAuthentication.vue";
import CustomWaiverStep from "./CustomWaiverStep";
import Withdraw from "./Withdraw.vue";

@Component({
  components: {
    PopupAuthentication,
    FinishedStep,
    RegistrarTypeChoice,
    'form-wrapper': form,
    'form-row': formRow,
    'input-row': input,
    'primary-button': primaryButton,
    'form-error': formError,
    SelectRow,
    Attachment,
    Modal,
    CopyEntry,
    StudentDetailsStep,
    GuardianDetailsStep,
    AddressDetailsStep,
    UploadStep,
    PermissionStep,
    UserAuthenticationFlow,
    AuthenticateCompetitor,
    CustomWaiverStep,
    Withdraw
  }
})
export default class EntryForm extends Mixins(UserMixin) {
  @Prop({default: 0}) readonly eventId!: number;

  @Prop() readonly entry!: EventEntryModel;

  @Prop() readonly startStep!: string;

  @Prop({default: 0}) readonly modalTop!: number;

  draft!: EventEntryModel;

  saving!: boolean;

  valid!: { [step: string]: boolean };

  STEPS = STEPS;

  currentStep = STEPS.ROLE;

  actualPreviousStep = STEPS.ROLE;

  confirmDelete = false;

  isClone = false;

  data(): { [propName: string]: unknown } {
    const valid: { [step: string]: boolean } = {};
    for (const i of Object.keys(STEPS)) {
      valid[STEPS[i]] = false;
    }

    this.createDraftFromEntry();

    return {
      draft: this.draft,
      saving: false,
      valid
    };
  }

  get showModalClose(): boolean {
    return this.currentStep !== STEPS.WITHDRAW;
  }

  get event(): EventModel {
    return this.$store.getters['events/byId'](this.entry.data.event_id);
  }

  get userCanSkipUploads(): boolean {
    if (this.draft.data.registrar_role === 'spectator') {
      return true;
    }

    return this.$store.getters['events/divisions/getById'](this.draft.data.division_id)?.data.skip_uploads ?? false;
  }

  get userIsEntryCreator(): boolean {
    return this.user.data.id === this.draft.data.user_id;
  }

  get requireWaiver(): boolean {
    if (this.draft.data.registrar_role === 'spectator') {
      return false;
    }

    return this.event.data.has_custom_waiver;
  }

  get formShouldBeDisabled(): boolean {
    if (!this.userIsEntryCreator && this.currentStep === STEPS.ROLE) {
      return true;
    }
    return this.entry.isWithdrawn() || this.event.data.deadline_edit_passed;
  }

  get nextStepID(): string {
    const isStudentRole = this.draft.data.registrar_role === 'student';
    const isSpectatorRole = this.draft.data.registrar_role === 'spectator';

    if (!this.userIsEntryCreator) {
      switch (this.currentStep) {
        case STEPS.COMPETITOR:
          return STEPS.UPLOAD;
        case STEPS.UPLOAD:
          return STEPS.COMPLETE;
      }
    }

    switch (this.currentStep) {
      case STEPS.ROLE:
        if (this.userAuthenticated) {
          if (isSpectatorRole) {
            return STEPS.ADDRESS;
          } else {
            return STEPS.COMPETITOR;
          }
        }
        return STEPS.AUTHENTICATION;
      case STEPS.AUTHENTICATION:
        return isSpectatorRole ? STEPS.ADDRESS : STEPS.COMPETITOR;
      case STEPS.COMPETITOR:
        return isStudentRole ? (this.userCanSkipUploads ? STEPS.ADDRESS : STEPS.UPLOAD) : STEPS.COMPETITOR_ACCOUNT;
      case STEPS.COMPETITOR_ACCOUNT:
        return this.userCanSkipUploads ? STEPS.ADDRESS : STEPS.UPLOAD;
      case STEPS.UPLOAD:
        return STEPS.ADDRESS;
      case STEPS.ADDRESS:
        return this.event.custom_waiver && this.requireWaiver ? STEPS.CUSTOM_WAIVER : STEPS.COMPLETE;
      case STEPS.CUSTOM_WAIVER:
        return STEPS.COMPLETE;
    }

    return STEPS.ROLE;
  }

  get previousStepID(): string {
    const isStudentRole = this.draft.data.registrar_role === 'student';
    const isSpectatorRole = this.draft.data.registrar_role === 'spectator';

    if (!this.userIsEntryCreator) {
      return STEPS.COMPETITOR;
    }

    switch (this.currentStep) {
      case STEPS.ROLE:
        return STEPS.ROLE;
      case STEPS.AUTHENTICATION:
        return STEPS.ROLE;
      case STEPS.COMPETITOR:
        return this.userAuthenticated ? STEPS.ROLE : STEPS.AUTHENTICATION;
      case STEPS.COMPETITOR_ACCOUNT:
        return STEPS.COMPETITOR;
      case STEPS.UPLOAD:
        return isStudentRole ? STEPS.COMPETITOR : STEPS.COMPETITOR_ACCOUNT;
      case STEPS.ADDRESS:
        if (isSpectatorRole) {
          return STEPS.ROLE;
        }
        return this.userCanSkipUploads ? STEPS.COMPETITOR : STEPS.UPLOAD;
      case STEPS.CUSTOM_WAIVER:
        return STEPS.ADDRESS;
      case STEPS.COMPLETE:
        return this.event.custom_waiver && this.requireWaiver ? STEPS.CUSTOM_WAIVER : STEPS.ADDRESS;
    }

    return STEPS.ROLE;
  }

  get hasPreviousStep(): boolean {
    return this.currentStep !== this.previousStepID;
  }

  get userAuthenticated(): boolean {
    const v = this.$store.getters['context/user'].isAuthenticated();
    this.valid[STEPS.AUTHENTICATION] = v;
    return v;
  }

  get showAuthenticationStep(): boolean {
    return !this.userAuthenticated
        || this.draft.data.registrar_role === 'guardian';
  }

  get studentDetails(): StudentDetails {
    return StudentDataMapper.fromModel(this.draft);
  }

  set studentDetails(details: StudentDetails) {
    StudentDataMapper.toModel(details, this.draft);
  }

  get guardianDetails(): GuardianDetails {
    return GuardianDataMapper.fromModel(this.draft);
  }

  set guardianDetails(details: GuardianDetails) {
    GuardianDataMapper.toModel(details, this.draft);
  }

  get addressDetails(): AddressDetails {
    return AddressDataMapper.fromModel(this.draft);
  }

  set addressDetails(details: AddressDetails) {
    AddressDataMapper.toModel(details, this.draft);
  }

  get studentErrors(): StudentDetails {
    return StudentDataMapper.errorsFromModel(this.draft);
  }

  get guardianErrors(): GuardianDetails {
    return GuardianDataMapper.errorsFromModel(this.draft);
  }

  get addressErrors(): AddressDetails {
    return AddressDataMapper.errorsFromModel(this.draft);
  }

  get uploadErrors(): Record<string, string> {
    return {
      attachment_references: this.draft.errors.attachment_references
    };
  }

  get permissionErrors(): Record<string, string> {
    return {
      waiver: this.draft.errors.waiver
    };
  }

  get submitButtonText(): string {
    if (this.formShouldBeDisabled) {
      return 'Next';
    }

    if (this.saving) {
      return '<span class="uk-icon-spin uk-icon-spinner"></span> ' + this.$t('events.form_button_saving');
    }

    if (this.draft && this.draft.data.id) {
      return <string>this.$t('events.entry_form_button_update');
    } else {
      return <string>this.$t('events.entry_form_button_continue');
    }
  }

  setValid(key: string, value: boolean): void {
    (this.valid as { [key: string]: boolean })[key] = value;
  }

  setValidAndContinue(key: string, value: boolean): void {
    this.setValid(key, value);
    this.nextStep();
  }

  created(): void {
    this.$store.dispatch('countries/fetchOptions')

    if (!this.userIsEntryCreator) {
      this.currentStep = STEPS.COMPETITOR;
      this.actualPreviousStep = "";
    }

    if (this.startStep) {
      this.currentStep = this.startStep;
    }

    if (this.entry) {
      this.createDraftFromEntry();
    }
  }

  createDraftFromEntry(): void {
    const data = JSON.parse(JSON.stringify(this.entry.data));
    if (!data.user_id) {
      data.user_id = this.$store.getters['context/user'].data.id;
    }
    this.draft = createEntry(data, JSON.parse(JSON.stringify(this.entry.errors)));

    if (this.entry.competitor) {
      Vue.set(this.draft, 'competitor', this.entry.competitor);
    }

    if (!this.draft.data.registrar_role) {
      this.draft.data.registrar_role = "student";
    }
  }

  previousStep(): void {
    if (this.draft.data.id && !this.event.data.deadline_edit_passed) {
      this.validateCurrentStep();
      if (this.currentStepIsValid()) {
        this.saveEntry();
      } else {
        return;
      }
    }

    if (this.hasPreviousStep) {
      this.actualPreviousStep = this.currentStep;
      this.currentStep = this.previousStepID;
    }
  }

  validateCurrentStep(silent = false): void {
    if (VALIDATION_STEPS[this.currentStep]) {
      for (const step of VALIDATION_STEPS[this.currentStep]) {
        if (this.$refs[step]) {
          (this.$refs[step] as any).runValidation(silent);
        }
      }
    } else {
      this.valid[this.currentStep] = true;
    }
  }

  currentStepIsValid(): boolean {
    if (VALIDATION_STEPS[this.currentStep]) {
      for (const step of VALIDATION_STEPS[this.currentStep]) {
        if (!this.valid[step]) {
          return false;
        }
      }
    }
    return true;
  }

  nextStep(): void {
    if (this.formShouldBeDisabled) {
      this.actualPreviousStep = this.currentStep;
      this.currentStep = this.nextStepID;
      return;
    }

    this.validateCurrentStep();

    this.actualPreviousStep = this.currentStep;

    if (this.currentStepIsValid()) {
      if ((this.nextStepID === STEPS.COMPLETE || this.entry.data.id) && !this.event.data.deadline_edit_passed) {
        this.saveEntry().then(() => {
          this.currentStep = this.nextStepID;
        });
      } else {
        this.currentStep = this.nextStepID;
      }
    }
  }

  revertStep(): void {
    this.currentStep = this.actualPreviousStep;
  }

  async saveEntry(): Promise<any> {
    this.saving = true;

    if (this.currentStep === STEPS.CUSTOM_WAIVER) {
      try {
        await this.$store.dispatch('events/waivers/save', this.entry.data.reference);

      } catch (e) {
        return;
      }
    }

    // Merge existing and new file uploads
    const attachmentReferences = [...this.draft.data.attachment_references, ...this.$store.getters['events/files/allFromEntry'](this.draft.data.reference)];
    this.draft.data.attachment_references = Array.from(new Set(attachmentReferences));

    return this.$store.dispatch('events/saveEntry', this.draft).then(() => {
      this.createDraftFromEntry();
    }).catch(() => {
      this.draft.errors = this.entry.errors;
    }).finally(() => {
      this.saving = false;
    })
  }

  close(): void {
    this.$emit('close-modal');
  }

  deleteAndClose(): void {
    this.$store.dispatch('events/deleteEntry', this.draft);
    this.$emit('deleted');
    this.close();
  }

  removeRef(ref: string): void {
    this.draft.data.attachment_references.splice(this.draft.data.attachment_references.indexOf(ref), 1);
    this.saveEntry();
  }

  removeEntry(): void {
    this.actualPreviousStep = this.currentStep;
    this.currentStep = STEPS.WITHDRAW;
  }

  checkBeforeClose(): void {
    if (!this.draft.data.id) {
      this.removeEntry();
    } else {
      this.close();
    }
  }

  useSelectedEntry(e: { value: string, label: string }): void {
    if (e.value.length === 0) {
      return;
    }

    this.$store.dispatch('events/copyEntry', {
      fromRef: e.value,
      toRef: this.draft.data.reference
    });

    this.isClone = true;

    this.createDraftFromEntry();

    this.validateCurrentStep();
  }

  get copyChoices(): { value: string, label: string }[] {
    const entries = this.$store.getters['events/getEntriesByEvent'](this.draft.data.event_id);
    const choices = [];
    const keys = [];
    for (const entry of entries) {
      if (entry.data.id && keys.indexOf(entry.getFullName()) < 0) {
        keys.push(entry.getFullName());
        choices.push({
          value: entry.data.reference,
          label: entry.getFullName()
        });
      }
    }
    return choices;
  }

  @Watch('currentStep', {immediate: true})
  onStepChange(nV: string, oV: string): void {
    this.$nextTick(() => {
      this.validateCurrentStep(!(this.entry.isSaved() || this.isClone));
    });
  }

  @Watch('userAuthenticated'/*, {immediate: true}*/)
  onUserAuth(nV: boolean, oV: boolean): void {
    if (nV && !oV) {
      if (this.draft.data.registrar_role === 'student') {
        this.draft.data.competitor_id = this.$store.getters['context/user'].data.id;
        this.valid[STEPS.COMPETITOR_ACCOUNT] = true;
      } else {
        this.valid[STEPS.COMPETITOR_ACCOUNT] = false;
      }
    }
  }

  @Watch('draft.data.registrar_role')
  resetGuardianDetails(nv: string, ov: string) {
    if (nv === 'spectator') {
      this.draft.data.division_id = this.event.data.spectator_division_id;
    }

    if (!ov) {
      return;
    }

    if (nv === 'student') {
      this.guardianDetails = {
        salutation: '',
        first_name: '',
        last_name: '',
      };
      this.draft.data.competitor_id = this.$store.getters['context/user'].data.id;
      this.valid[STEPS.GUARDIAN] = true;
    } else {
      this.draft.data.competitor_id = 0;
      this.valid[STEPS.GUARDIAN] = false;
    }
  }

}
</script>

<style scoped>
.efDeleteButton--disabled {
  border-color: #7d7d7d !important;
  color: #7d7d7d !important;
  cursor: default !important;
}
.efDeleteMessage {
  color: #7d7d7d;
  margin-left: 0.5em;
}
.efSubmit--disabled {
  opacity: 0.4;
  cursor: default;
}
.efFooter {
  display: flex;
  align-items: center;
  flex-flow: wrap;
}
.efFooter__row {
  flex: 1;
  display: flex;
  white-space: nowrap;
}
.efNav {
  justify-content: flex-end;
}
@media only screen and (max-width: 480px) {
  .efFooter {
    flex-flow: wrap-reverse;
  }
  .efFooter__row:first-of-type {
    margin-top: 1em;
  }
}
</style>
